'use client';

import { Bookmark, Check } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@components/ui/button';
import type { DefaultSaveStatus } from '@hooks/use-knowledge-search';

export type KnowledgeSearchDefaultControlsProps = {
  term: string;
  savedQuery: string | null;
  saveStatus: DefaultSaveStatus;
  defaultPending: boolean;
  onSave: () => void;
  onReset: () => void;
  onChoose: () => void;
  onEdit: () => void;
};

export default function KnowledgeSearchDefaultControls({
  term,
  savedQuery,
  saveStatus,
  defaultPending,
  onSave,
  onReset,
  onChoose,
  onEdit
}: KnowledgeSearchDefaultControlsProps) {
  const t = useTranslations('knowledge-search');
  const trimmed = term.trim();
  const saving = defaultPending;
  const feedback = (
    <p
      role={saveStatus === 'error' ? 'alert' : 'status'}
      className="text-caption text-muted-foreground"
    >
      {saveStatus === 'error'
        ? t('defaultSaveError')
        : saveStatus === 'saved'
          ? t('defaultSaved')
          : saving
            ? t('defaultSaving')
            : null}
    </p>
  );

  if (trimmed) {
    if (trimmed.length < 2) return null;
    const saved = savedQuery === trimmed && saveStatus !== 'error';
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 lg:px-6">
        {feedback}
        <Button
          size="sm"
          variant="ghost"
          disabled={saving || saved}
          onClick={onSave}
        >
          {saved ? (
            <Check aria-hidden="true" />
          ) : (
            <Bookmark aria-hidden="true" />
          )}
          {saving
            ? t('defaultSaving')
            : saved
              ? t('defaultSaved')
              : t('saveAsDefault')}
        </Button>
      </div>
    );
  }

  return (
    <section aria-label={t('defaultTitle')} className="rounded-xl bg-card p-6">
      <div className="flex items-center gap-2 text-foreground">
        <Bookmark
          className="size-5 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <h3 className="text-card-title font-medium">{t('defaultTitle')}</h3>
      </div>
      <p className="mt-2 text-caption text-muted-foreground">
        {t('defaultDescription')}
      </p>
      {savedQuery ? (
        <p className="mt-3 break-words text-section-title">{savedQuery}</p>
      ) : null}
      <div className="mt-4 flex flex-col gap-2 lg:flex-row lg:items-center">
        <Button
          variant="primary"
          disabled={saving}
          onClick={savedQuery ? onEdit : onChoose}
        >
          {savedQuery ? t('editSearch') : t('chooseDefault')}
        </Button>
        {savedQuery ? (
          <Button variant="ghost" disabled={saving} onClick={onReset}>
            {saving ? t('defaultSaving') : t('resetDefaultSearch')}
          </Button>
        ) : (
          <p className="text-center text-caption text-muted-foreground lg:text-left">
            {t('defaultHint')}
          </p>
        )}
      </div>
      {saveStatus !== 'idle' ? <div className="mt-2">{feedback}</div> : null}
    </section>
  );
}
